import { useEffect, useState } from "react";

const INSTALLED_KEY = "pwaInstalled";
const DISMISSED_KEY = "pwaInstallDismissed";

const detectIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const detectSafari = () => {
  const ua = navigator.userAgent;
  return (
    ua.includes("Safari") && !ua.includes("Chrome") && !ua.includes("CriOS")
  );
};

const detectIosChrome = () => {
  const ua = navigator.userAgent;
  return detectIos() && (ua.includes("CriOS") || ua.includes("Chrome"));
};

const detectMac = () =>
  navigator.userAgent.toLowerCase().includes("mac os x") && !detectIos();

export default function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [installError, setInstallError] = useState("");
  const [isDismissed, setIsDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === "1",
  );
  const [isInstalled, setIsInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [isIosChrome, setIsIosChrome] = useState(false);
  const [isMac, setIsMac] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(display-mode: standalone)");

    const markInstalled = () => {
      localStorage.setItem(INSTALLED_KEY, "1");
      localStorage.setItem(DISMISSED_KEY, "1");
      setIsInstalled(true);
      setIsDismissed(true);
      setDeferredPrompt(null);
      setIsInstallable(false);
      setInstallError("");
    };

    const standaloneNow =
      mediaQuery.matches || window.navigator.standalone === true;

    setIsStandalone(standaloneNow);
    if (standaloneNow) {
      markInstalled();
    } else {
      const wasInstalled = localStorage.getItem(INSTALLED_KEY) === "1";
      const wasDismissed = localStorage.getItem(DISMISSED_KEY) === "1";
      if (wasInstalled) setIsInstalled(true);
      if (wasInstalled || wasDismissed) setIsDismissed(true);
    }

    setIsIos(detectIos());
    setIsSafari(detectSafari());
    setIsIosChrome(detectIosChrome());
    setIsMac(detectMac());

    const checkRelatedApps = async () => {
      if (typeof navigator.getInstalledRelatedApps !== "function") return;
      try {
        const relatedApps = await navigator.getInstalledRelatedApps();
        if (relatedApps && relatedApps.length > 0) {
          markInstalled();
        }
      } catch (error) {
        // ignore
      }
    };

    checkRelatedApps();

    const handleBeforeInstallPrompt = (event) => {
      const dismissed = localStorage.getItem(DISMISSED_KEY) === "1";
      const installed = localStorage.getItem(INSTALLED_KEY) === "1";
      if (dismissed || installed) {
        setIsDismissed(dismissed || installed);
        setIsInstallable(false);
        return;
      }
      event.preventDefault();
      setDeferredPrompt(event);
      setIsInstallable(true);
      setInstallError("");
    };

    const handleAppInstalled = () => {
      markInstalled();
    };

    const handleDisplayModeChange = (event) => {
      setIsStandalone(event.matches);
      if (event.matches) {
        markInstalled();
      }
    };

    mediaQuery.addEventListener("change", handleDisplayModeChange);
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      mediaQuery.removeEventListener("change", handleDisplayModeChange);
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt || typeof deferredPrompt.prompt !== "function") {
      setInstallError(
        "Install prompt isn't available. Please use the browser menu to install.",
      );
      return;
    }

    try {
      setInstallError("");
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "dismissed") {
        setInstallError(
          "Install was dismissed. You can install anytime from the browser menu.",
        );
      }
      if (choice?.outcome === "accepted") {
        localStorage.setItem(DISMISSED_KEY, "1");
        localStorage.setItem(INSTALLED_KEY, "1");
        setIsDismissed(true);
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
      setIsInstallable(false);
    } catch (error) {
      setInstallError(
        "We couldn't open the install dialog. Please use the browser menu to install.",
      );
    }
  };

  const markAsInstalled = () => {
    localStorage.setItem(INSTALLED_KEY, "1");
    localStorage.setItem(DISMISSED_KEY, "1");
    setIsInstalled(true);
    setIsDismissed(true);
    setDeferredPrompt(null);
    setIsInstallable(false);
    setInstallError("");
  };

  const dismissInstall = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setIsDismissed(true);
    setDeferredPrompt(null);
    setIsInstallable(false);
  };

  const clearInstallError = () => setInstallError("");

  return {
    deferredPrompt,
    isInstallable,
    isDismissed,
    isInstalled,
    isStandalone,
    isIos,
    isSafari,
    isIosChrome,
    isMac,
    promptInstall,
    dismissInstall,
    installError,
    clearInstallError,
    markAsInstalled,
  };
}
